import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { RiCloseLine, RiDownloadLine, RiFileLine, RiLoader4Line } from "react-icons/ri";
import { fetchClientsData } from "../../Redux/Thunks/clientsThunk";
import {
  updateProject,
  fetchProjectsData,
  fetchProjectTags,
  fetchProjectById,
  fetchProjectAttachments,
  getProjectAttachmentDownloadUrl,
  deleteProjectAttachment,
} from "../../Redux/Thunks/projectManagementThunk";
import {
  selectProjectDetailRaw,
  selectProjectDetailLoading,
  selectProjectDetailError,
  selectProjectTagsState,
} from "../../Redux/Slices/projectsManagementSlice";
import {
  selectClientsData,
  selectClientsLoading,
  selectClientsError,
} from "../../Redux/Slices/clientsSlice";
import { AddProjectSchema } from "../../FormsValidations/Validations";
import Input from "../../Utility/Input/Input";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import FormSubmitButton from "../../Utility/Button/FormSubmitButton";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import FileDropzone from "../../Utility/FileDropzone/FileDropzone";
import ErrorMsg from "../../Utility/ErrorMsg";
import SingleDatePicker from "../../Utility/DatePickerRange/SingleDatePicker";
import { DISPLAY_DATE_FORMAT_PICKER_MOMENT } from "../../utils/dateUtils";
import { logger } from "../../utils/logger";
import { showSuccess, showError } from "../../utils/toast";
import { validateUploadFiles } from "../../utils/fileUploadValidation";
import { usePermissions } from "@/hooks/usePermissions";
import { APP_ROLE } from "../../permissions/roleKinds";
import { getListFromApiResponse, formatProjectStatusLabel } from "./projectMappers";
import {
  normalizeProjectFromApi,
  unwrapProjectDetailPayload,
} from "./projectManagementNormalizers";
import { fetchSystemList } from "../../Redux/Thunks/systemListsThunk";
import {
  selectSystemListItems,
  selectSystemListStateForType,
} from "../../Redux/Slices/systemListsSlice";
import { parseProjectTagsPayload, systemListRowsToTagIds } from "./resolveProjectTags";
import { mapSystemListItemToOption } from "../Issues/issueMappers";
import "../Clients/clients.css";
import BackArrow from "../../Utility/BackArrow/BackArrow";
import NoDataFound from "../../Utility/NoDataFound/NoDataFound";
import { ROUTES } from "../../constants";
import { ContentLoader } from "../../Utility/Loader";

export default function EditProject() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const clientsData = useSelector(selectClientsData);
  const clientsLoading = useSelector(selectClientsLoading);
  const clientsError = useSelector(selectClientsError);
  const projectDetailRaw = useSelector(selectProjectDetailRaw);
  const loadingProject = useSelector(selectProjectDetailLoading);
  const projectDetailError = useSelector(selectProjectDetailError);
  const projectTagsState = useSelector(selectProjectTagsState);
  const clientTagListState = useSelector((s) => selectSystemListStateForType(s, "CLIENT_TAG"));
  const vendorTagListState = useSelector((s) => selectSystemListStateForType(s, "VENDOR_TAG"));
  const clientTagList = useSelector((s) => selectSystemListItems(s, "CLIENT_TAG")) ?? [];
  const vendorTagList = useSelector((s) => selectSystemListItems(s, "VENDOR_TAG")) ?? [];
  const languageList = useSelector((s) => selectSystemListItems(s, "LANGUAGE")) ?? [];
  const prevPath = location.state?.previousPath ?? ROUTES.PROJECTS;

  const project = useMemo(() => {
    if (!id || !projectDetailRaw) return null;
    const inner = unwrapProjectDetailPayload(projectDetailRaw);
    if (!inner || typeof inner !== "object") return null;
    const normalized = normalizeProjectFromApi(inner);
    const pid = normalized?.id ?? inner?.id;
    if (pid == null || String(pid) !== String(id)) return null;
    return normalized;
  }, [id, projectDetailRaw]);

  const { roleKind } = usePermissions();
  const canEditBillingNotes = roleKind === APP_ROLE.ADMIN;

  const [clientTagIds, setClientTagIds] = useState([]);
  const [vendorTagIds, setVendorTagIds] = useState([]);
  const [languageId, setLanguageId] = useState("");
  const [documents, setDocuments] = useState([]);
  const [projectAttachments, setProjectAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const [clientNotes, setClientNotes] = useState("");
  const [vendorNotes, setVendorNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(AddProjectSchema),
    defaultValues: {
      clientName: "",
      projectKey: "",
      projectName: "",
      startDate: "",
      endDate: "",
      hoursLimitPeriod: "Monthly",
      hoursLimit: "",
      status: "Active",
      version: "",
    },
  });

  useEffect(() => {
    setClientTagIds([]);
    setVendorTagIds([]);
    setLanguageId("");
  }, [id]);

  const clientTagOptions = useMemo(
    () => clientTagList.map((item) => mapSystemListItemToOption(item, {})),
    [clientTagList]
  );
  const vendorTagOptions = useMemo(
    () => vendorTagList.map((item) => mapSystemListItemToOption(item, {})),
    [vendorTagList]
  );
  const languageOptions = useMemo(
    () => languageList.map((item) => mapSystemListItemToOption(item, {})),
    [languageList]
  );

  useEffect(() => {
    dispatch(fetchClientsData({ getAll: true }));
    dispatch(
      fetchSystemList({
        listType: "CLIENT_TAG",
        context: "edit-project-client-tags",
        force: true,
      })
    );
    dispatch(
      fetchSystemList({
        listType: "VENDOR_TAG",
        context: "edit-project-vendor-tags",
        force: true,
      })
    );
    dispatch(
      fetchSystemList({
        listType: "LANGUAGE",
        context: "edit-project-language",
        force: true,
      })
    );
  }, [dispatch]);

  /** Always load project by id so refresh / deep link works (projects list may be empty). */
  useEffect(() => {
    if (!id) return;
    dispatch(fetchProjectById(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (project) {
      reset({
        clientName: project.clientName ?? "",
        projectKey: project.projectKey ?? "",
        projectName: project.projectName ?? "",
        startDate: project.startDate ?? project.receivedDate ?? project.eta ?? "",
        endDate: project.endDate ?? project.eta ?? "",
        hoursLimitPeriod: project.hoursLimitPeriod ?? "Monthly",
        hoursLimit: project.hoursLimit != null ? String(project.hoursLimit) : "",
        status: formatProjectStatusLabel(project.status ?? "ACTIVE"),
        version: project.version ?? "",
      });
      setClientNotes(project.clientNotes ?? "");
      setVendorNotes(project.vendorNotes ?? "");
    }
  }, [project, reset]);

  useEffect(() => {
    if (!id) return;
    dispatch(fetchProjectTags(id));
  }, [id, dispatch]);

  // Tags: GET /projects/:id/tags (system list rows)
  useEffect(() => {
    if (!id || projectTagsState.projectId !== id) return;
    if (projectTagsState.loading) return;
    if (projectTagsState.error) return;
    const data = projectTagsState.raw;
    if (data == null) return;
    const { clientTags, vendorTags, languageTags } = parseProjectTagsPayload(data);
    setClientTagIds(systemListRowsToTagIds(clientTags));
    setVendorTagIds(systemListRowsToTagIds(vendorTags));
    setLanguageId(systemListRowsToTagIds(languageTags)[0] ?? "");
  }, [
    id,
    projectTagsState.loading,
    projectTagsState.error,
    projectTagsState.raw,
    projectTagsState.projectId,
  ]);

  // Documents — GET/DELETE /projects/:id/attachments (uploads travel with create/update, see onSubmit)
  const loadAttachments = useCallback(() => {
    if (!id) return;
    setAttachmentsLoading(true);
    dispatch(fetchProjectAttachments(id))
      .unwrap()
      .then((raw) => setProjectAttachments(getListFromApiResponse(raw)))
      .catch(() => setProjectAttachments([]))
      .finally(() => setAttachmentsLoading(false));
  }, [id, dispatch]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const handleDownloadAttachment = async (attachment) => {
    const attachmentId = attachment?.id ?? attachment?.attachmentId;
    if (!id || !attachmentId) return;
    try {
      const raw = await dispatch(
        getProjectAttachmentDownloadUrl({ projectId: id, attachmentId, expiresIn: 3600 })
      ).unwrap();
      const url = raw?.data?.url ?? raw?.url ?? null;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else showError("Could not get download URL.");
    } catch (error) {
      showError(error?.message || "Failed to get download link.");
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    const attachmentId = attachment?.id ?? attachment?.attachmentId;
    if (!id || !attachmentId) return;
    setDeletingAttachmentId(String(attachmentId));
    try {
      await dispatch(deleteProjectAttachment({ projectId: id, attachmentId })).unwrap();
      loadAttachments();
      showSuccess("Document deleted.");
    } catch (error) {
      showError(error?.message || "Failed to delete document.");
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const startDate = watch("startDate");

  const onSubmit = async (data) => {
    if (!id) return;

    if (documents.length > 10) {
      showError("Maximum 10 files allowed.");
      return;
    }
    if (documents.length > 0) {
      const documentValidationError = await validateUploadFiles(documents, {
        inspectContent: false,
      });
      if (documentValidationError) {
        showError(documentValidationError);
        return;
      }
    }

    setLoading(true);
    try {
      const raw = {
        name: data.projectName?.trim(),
        hoursLimit:
          data.hoursLimit != null && data.hoursLimit !== "" ? Number(data.hoursLimit) : undefined,
        startDate: data.startDate || undefined,
        // endDate/version/notes support explicit null to clear — omitting would leave them unchanged
        endDate: data.endDate ? data.endDate : null,
        hoursLimitPeriod: data.hoursLimitPeriod
          ? data.hoursLimitPeriod.toUpperCase().replace(/\s+/g, "_")
          : undefined,
        status: data.status ? data.status.toUpperCase().replace(/\s+/g, "_") : undefined,
        clientTagIds: clientTagIds.map((x) => String(x)).filter(Boolean),
        vendorTagIds: vendorTagIds.map((x) => String(x)).filter(Boolean),
        languageIds: languageId ? [String(languageId)] : [],
        version: data.version?.trim() ? data.version.trim() : null,
        ...(canEditBillingNotes && {
          clientNotes: clientNotes.trim() ? clientNotes.trim() : null,
          vendorNotes: vendorNotes.trim() ? vendorNotes.trim() : null,
        }),
      };
      const projectData = Object.fromEntries(
        Object.entries(raw).filter(([, v]) => v !== undefined)
      );
      await dispatch(updateProject({ id, projectData, files: documents })).unwrap();
      showSuccess("Project updated successfully");
      await dispatch(fetchProjectsData({ getAll: true, force: true })).unwrap();
      navigate(prevPath);
    } catch (error) {
      logger.error("Error updating project:", error);
      const message =
        error?.message || error?.response?.data?.message || "Failed to update project.";
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const clientList = getListFromApiResponse(clientsData);
  const clientNames = clientList.map((c) => c.clientName ?? c.name).filter(Boolean);

  const tagsLoading = projectTagsState.loading;
  const clientTagsReady = clientTagListState?.status === "succeeded";
  const vendorTagsReady = vendorTagListState?.status === "succeeded";
  const projectTagsReady =
    !projectTagsState.loading &&
    projectTagsState.error == null &&
    projectTagsState.raw != null &&
    projectTagsState.projectId === id;

  const depsError =
    clientsError != null ||
    clientTagListState?.status === "failed" ||
    vendorTagListState?.status === "failed" ||
    projectTagsState.error != null;

  if (id && loadingProject) {
    return (
      <div className="p-4">
        <BackArrow to={prevPath} />
        <ContentLoader loading className="min-h-[240px]" />
      </div>
    );
  }

  if (
    id &&
    !project &&
    !loadingProject &&
    (projectDetailError != null || (projectDetailRaw != null && project == null))
  ) {
    return (
      <div className="p-4">
        <BackArrow to={prevPath} />
        <NoDataFound message="Project not found." />
      </div>
    );
  }

  const pageReady =
    !loadingProject &&
    project != null &&
    !clientsLoading &&
    clientsData != null &&
    clientTagsReady &&
    vendorTagsReady &&
    projectTagsReady &&
    !depsError;

  if (!pageReady) {
    const msg = depsError ? "Failed to load required form data." : null;
    return (
      <div className="p-4">
        <BackArrow to={prevPath} />
        {msg ? <NoDataFound message={msg} /> : <ContentLoader loading className="min-h-[240px]" />}
      </div>
    );
  }

  return (
    <div>
      <div className="w-full">
        <div className="p-0">
          <div className="w-full">
            <div className="flex flex-wrap -mx-4 mt-2 mb-2 items-center justify-between">
              <div className="w-full px-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <BackArrow to={prevPath} />
                  <Typography variant="h4" className="mb-0 text-theme-primary">
                    Edit Project
                  </Typography>
                </div>
              </div>
            </div>

            <div className="relative flex flex-col min-w-0 bg-surface-primary rounded-md border border-border-primary shadow-sm mt-2 mb-5">
              <div className="flex-1 p-6">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="flex flex-wrap gap-4">
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Controller
                        name="clientName"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            {...field}
                            label="Client"
                            options={clientNames}
                            placeholder="Select Client"
                            error={errors.clientName?.message}
                            required={true}
                            searchable={true}
                            className="text-sm"
                          />
                        )}
                      />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="projectKey"
                        className="font-medium text-text-primary mb-1"
                      >
                        Project Key
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("projectKey")}
                          readOnly
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-secondary bg-surface-secondary cursor-not-allowed"
                          id="projectKey"
                          placeholder="e.g. ONBRD"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.projectKey?.message} className="mt-1" />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="projectName"
                        className="font-medium text-text-primary mb-1"
                      >
                        Project Name
                        <Typography variant="span1" className="text-red-600 pl-1" component="span">
                          *
                        </Typography>
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("projectName")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.projectName ? "border-badge-danger" : ""
                          }`}
                          id="projectName"
                          placeholder="Enter project name"
                          label=""
                          error={errors.projectName?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.projectName?.message} className="mt-1" />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography variant="text2" className="font-medium text-text-primary mb-1">
                        Start Date
                        <Typography variant="span1" className="text-red-600 pl-1" component="span">
                          *
                        </Typography>
                      </Typography>
                      <Controller
                        name="startDate"
                        control={control}
                        render={({ field }) => (
                          <SingleDatePicker
                            value={field.value ? { date: field.value } : null}
                            onChange={(val) => field.onChange(val?.date ?? "")}
                            placeholder={DISPLAY_DATE_FORMAT_PICKER_MOMENT}
                            className={errors.startDate ? "!border-red-500" : ""}
                          />
                        )}
                      />
                      <ErrorMsg message={errors.startDate?.message} className="mt-1" />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography variant="text2" className="font-medium text-text-primary mb-1">
                        End Date
                      </Typography>
                      <Controller
                        name="endDate"
                        control={control}
                        render={({ field }) => (
                          <SingleDatePicker
                            value={field.value ? { date: field.value } : null}
                            onChange={(val) => field.onChange(val?.date ?? "")}
                            minDate={startDate || undefined}
                            placeholder={DISPLAY_DATE_FORMAT_PICKER_MOMENT}
                            className={errors.endDate ? "!border-red-500" : ""}
                          />
                        )}
                      />
                      <ErrorMsg message={errors.endDate?.message} className="mt-1" />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Controller
                        name="hoursLimitPeriod"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            {...field}
                            label="Hours Limit Period"
                            options={["Monthly", "Hourly", "Retainer", "Fixed"]}
                            // options={["Monthly"]}
                            placeholder="Select Hours Limit Period"
                            error={errors.hoursLimitPeriod?.message}
                            required={true}
                            searchable={false}
                            className="text-sm"
                          />
                        )}
                      />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="hoursLimit"
                        className="font-medium text-text-primary mb-1"
                      >
                        Hours Limit
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="number"
                          {...register("hoursLimit")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.hoursLimit ? "border-badge-danger" : ""
                          }`}
                          id="hoursLimit"
                          placeholder="Enter hours limit"
                          min="0"
                          label=""
                          error={errors.hoursLimit?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.hoursLimit?.message} className="mt-1" />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            {...field}
                            label="Status"
                            options={["Active", "Completed", "On Hold", "Archived"]}
                            placeholder="Select Status"
                            error={errors.status?.message}
                            required={true}
                            searchable={false}
                            className="text-sm"
                          />
                        )}
                      />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        className="font-medium text-text-primary block mb-1"
                      >
                        Client Tags
                      </Typography>
                      <Dropdown
                        name="clientTagIds"
                        value={clientTagIds}
                        onChange={(e) =>
                          setClientTagIds(Array.isArray(e.target?.value) ? e.target.value : [])
                        }
                        options={clientTagOptions}
                        placeholder={
                          tagsLoading ? "Loading tags…" : "Search and select client tags"
                        }
                        multiselect
                        searchable
                        disabled={tagsLoading}
                        noWrapper
                        className="text-sm w-full"
                      />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        className="font-medium text-text-primary block mb-1"
                      >
                        Vendor Tags
                      </Typography>
                      <Dropdown
                        name="vendorTagIds"
                        value={vendorTagIds}
                        onChange={(e) =>
                          setVendorTagIds(Array.isArray(e.target?.value) ? e.target.value : [])
                        }
                        options={vendorTagOptions}
                        placeholder={
                          tagsLoading ? "Loading tags…" : "Search and select vendor tags"
                        }
                        multiselect
                        searchable
                        disabled={tagsLoading}
                        noWrapper
                        className="text-sm w-full"
                      />
                    </div>

                    {/* Language */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        className="font-medium text-text-primary block"
                      >
                        Script Language
                      </Typography>
                      <Dropdown
                        name="languageId"
                        value={languageId}
                        onChange={(e) => setLanguageId(e.target?.value ?? "")}
                        options={languageOptions}
                        placeholder="Search and select language"
                        searchable
                        noWrapper
                        className="text-sm w-full"
                      />
                    </div>

                    {/* Version */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="version"
                        className="font-medium text-text-primary mb-1"
                      >
                        Version
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("version")}
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                          id="version"
                          placeholder="Enter version"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                    </div>

                    {/* Documents — existing uploads (download/delete) + add more */}
                    <div className="w-full mb-2">
                      <Typography
                        variant="text2"
                        className="font-medium text-text-primary mb-2 block"
                      >
                        Documents
                      </Typography>
                      {attachmentsLoading ? (
                        <div className="text-sm text-text-secondary py-2">Loading documents…</div>
                      ) : projectAttachments.length > 0 ? (
                        <ul className="space-y-2 mb-3">
                          {projectAttachments.map((att) => {
                            const attId = att.id ?? att.attachmentId;
                            const name = att.originalName ?? att.name ?? att.fileName ?? "Document";
                            return (
                              <li
                                key={attId}
                                className="flex items-center justify-between p-2 bg-surface-secondary border border-border-primary rounded-md group"
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <RiFileLine className="text-text-secondary shrink-0" />
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadAttachment(att)}
                                    className="text-left min-w-0 flex-1 text-brand-primary hover:underline truncate"
                                  >
                                    {name}
                                  </button>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownloadAttachment(att)}
                                    className="shrink-0 !p-1"
                                    title="Download"
                                    disabled={deletingAttachmentId === String(attId)}
                                  >
                                    <RiDownloadLine className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDeleteAttachment(att)}
                                    className="shrink-0 !p-1"
                                    title={
                                      deletingAttachmentId === String(attId)
                                        ? "Deleting…"
                                        : "Delete"
                                    }
                                    disabled={deletingAttachmentId === String(attId)}
                                  >
                                    {deletingAttachmentId === String(attId) ? (
                                      <RiLoader4Line className="w-4 h-4 animate-spin" aria-hidden />
                                    ) : (
                                      <RiCloseLine className="w-4 h-4" aria-hidden />
                                    )}
                                  </Button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <div className="text-sm text-text-secondary py-2 mb-2">
                          No documents yet.
                        </div>
                      )}
                      <FileDropzone
                        label="Add more documents"
                        onFilesChange={(files) => setDocuments(files)}
                        maxFiles={10}
                        maxSizeMB={10}
                        inspectContent={false}
                      />
                    </div>

                    {canEditBillingNotes && (
                      <>
                        <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                          <Typography
                            variant="text2"
                            component="label"
                            className="font-medium text-text-primary block mb-1"
                          >
                            Client Billing Notes
                            <Typography
                              variant="span1"
                              className="text-text-secondary text-xs pl-2"
                              component="span"
                            >
                              (Admin only)
                            </Typography>
                          </Typography>
                          <textarea
                            value={clientNotes}
                            onChange={(e) => setClientNotes(e.target.value)}
                            rows={3}
                            placeholder="e.g. 20 hours billable to client"
                            className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary text-sm resize-y"
                          />
                        </div>

                        <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                          <Typography
                            variant="text2"
                            component="label"
                            className="font-medium text-text-primary block mb-1"
                          >
                            Vendor Billing Notes
                            <Typography
                              variant="span1"
                              className="text-text-secondary text-xs pl-2"
                              component="span"
                            >
                              (Admin only)
                            </Typography>
                          </Typography>
                          <textarea
                            value={vendorNotes}
                            onChange={(e) => setVendorNotes(e.target.value)}
                            rows={3}
                            placeholder="e.g. 45 hours — contractor"
                            className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary text-sm resize-y"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex flex-wrap -mx-4 mt-3">
                    <div className="w-full px-4 text-end">
                      <div className="flex gap-2 justify-end">
                        <Button
                          type="button"
                          variant="danger"
                          size="md"
                          disabled={loading}
                          onClick={() => navigate(prevPath)}
                          className="btn-reset"
                        >
                          Cancel
                        </Button>
                        <FormSubmitButton
                          loading={loading}
                          disabled={tagsLoading}
                          label="Update Project"
                          idleIconClass="ri-save-line"
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
